import { useState } from 'react'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { SettingsSaveError } from '@/lib/settingsSaveError'
import type { LiffTheme } from '@/types'

const DEFAULT_LIFF_THEME: Required<LiffTheme> = {
  primary_color: '#2d6b6b',
  button_color: '#2d6b6b',
  login_bg_color: '#ffffff',
  login_bg_image: '',
  login_panel_enabled: 'false',
  login_panel_color: '#ffffff',
  login_panel_opacity: '0.85',
  // Empty: unset, so the login page keeps its original look until changed.
  login_title_align: '',
  login_panel_margin: '',
  login_panel_padding: '',
  login_panel_radius: '',
  home_hero_color: '#2d6b6b',
  home_hero_image: '',
  font_family: '',
}

export function useLiffSettings() {
  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()
  const translateError = useApiErrorTranslator('settings')
  // PAY-1 (T149-084): no client validation here (none invented), so this is
  // only ever set on a server failure. Cleared on the next save attempt.
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  const serverLiffTheme: Required<LiffTheme> = {
    ...DEFAULT_LIFF_THEME,
    ...account?.settings.liff_theme,
  }

  const [localLiffTheme, setLocalLiffTheme] = useState<Required<LiffTheme> | null>(null)

  const liffTheme = localLiffTheme ?? serverLiffTheme

  const updateThemeField = (field: keyof LiffTheme, value: string) => {
    setLocalLiffTheme((prev) => ({
      ...(prev ?? serverLiffTheme),
      [field]: value,
    }))
  }

  // PAY-1 (T149-084): returns true only when the save actually happened.
  // The caller shows success only on true.
  const handleSave = async (e: React.FormEvent): Promise<boolean> => {
    e.preventDefault()
    setSaveError(null)
    try {
      await updateSettings.mutateAsync({
        // EP-156: liff_id is saved only by the LINE OA card.
        settings: {
          liff_theme: liffTheme,
        },
      })
      return true
    } catch (err) {
      setSaveError({ serverMessage: translateError(err) })
      return false
    }
  }

  return {
    liffTheme,
    isPending: updateSettings.isPending,
    saveError,
    updateThemeField,
    handleSave,
  }
}
